import { every, isNil, noop } from 'lodash-es';
import { Children, createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import styled from 'styled-components';
import { Z_INDEX } from '../../styles/layout';
import { Button } from '../Button/Button';
import { IconButton, type IconButtonProps } from '../Button/IconButton';
import type { ButtonProps, ButtonVariants } from '../Button/styles';
import { Box, Flex } from '../Core';
import { FormControlSizes } from '../Form/types';
import { IconName } from '../Icons';
import { Popover, usePopoverState } from '../Popover';

const MenuContext = createContext<MenuContextProps | undefined>(undefined);
MenuContext.displayName = 'MenuContext';

interface MenuContextProps {
  closeOnSelect: boolean;
  close(): void;
}

interface MenuProps {
  children?: ReactNode;
  /** Content to use as the menu button instead of the icon */
  triggerContent?: ReactNode;
  triggerIcon?: IconName;
  triggerMinimal?: boolean;
  triggerRound?: boolean;
  triggerVariant?: ButtonVariants;
  closeOnSelect?: boolean;
  menuButtonIconProps?: Partial<IconButtonProps>;
  /** default menu popover is Z_INDEX.popover - can be adjusted as needed  */
  menuPopoverZIndex?: keyof typeof Z_INDEX;
  'data-testid'?: string;
}

export function Menu({
  children,
  triggerContent,
  triggerIcon = IconName.DotsVertical,
  triggerMinimal = true,
  triggerRound = false,
  triggerVariant,
  closeOnSelect = true,
  'data-testid': dataTestId,
  menuButtonIconProps,
  menuPopoverZIndex,
}: MenuProps) {
  const popover = usePopoverState({
    trigger: 'click',
    usePortal: true,
    placement: 'bottom-end',
    closeOnClickOutside: true,
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top-end'],
        },
      },
    ],
  });
  const popoverProps = useMemo(() => {
    if (menuPopoverZIndex) {
      return {
        ...popover,
        zIndex: Z_INDEX[menuPopoverZIndex],
      };
    }
    return popover;
  }, [menuPopoverZIndex, popover]);

  const { onClickTarget, close } = popover;

  const handleClickTarget = useCallback(
    (e: Event) => {
      onClickTarget(e);
      e.stopPropagation();
    },
    [onClickTarget]
  );

  if (every(Children.toArray(children), child => isNil(child))) {
    return null;
  }

  return (
    <MenuContext.Provider value={{ closeOnSelect, close }}>
      <Flex alignItems="center" data-testid={dataTestId}>
        <Popover {...popoverProps} onClickTarget={handleClickTarget}>
          {triggerContent ? (
            <Box cursor="pointer">{triggerContent}</Box>
          ) : (
            <IconButton
              size={FormControlSizes.Small}
              icon={triggerIcon}
              ghost={triggerMinimal}
              round={triggerRound}
              variant={triggerVariant}
              data-testid="menu-icon-button-trigger"
              prominent
              {...menuButtonIconProps}
            />
          )}
          <Flex flexDirection="column" alignItems="stretch" gap="spacing4">
            {children}
          </Flex>
        </Popover>
      </Flex>
    </MenuContext.Provider>
  );
}

export const MenuGroup = styled(Flex).attrs({
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 'spacing4',
})`
  padding: calc(var(--spacing8) * 1px) 0;
  border-top: 1px solid var(--borderColorDivider);
  border-bottom: 1px solid var(--borderColorDivider);
  margin: calc(var(--spacing4) * 1px) 0;
  &:first-child {
    padding-top: 0;
    margin-top: 0;
  }
  &:first-child,
  & + & {
    border-top: none;
    margin-top: 0;
  }
  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
    margin-bottom: 0;
  }
`;

// Default values for the menu context in case the context is not provided
const DEFAULT_MENU_CONTEXT_VALUES: MenuContextProps = {
  closeOnSelect: true,
  close: noop,
};

export const MenuItem = styled(function MenuItem({
  onClick,
  as,
  ...props
}: ButtonProps & { as?: Parameters<typeof Button>[0]['as'] }) {
  const { closeOnSelect, close } = useContext(MenuContext) ?? DEFAULT_MENU_CONTEXT_VALUES;
  const handleClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    e => {
      onClick?.(e);
      if (closeOnSelect) {
        close();
      }
    },
    [onClick, close, closeOnSelect]
  );
  return <Button forwardedAs={as} ghost={true} {...props} onClick={handleClick} />;
})`
  && {
    justify-content: flex-start;
    span + i {
      margin-left: auto;
    }
  }
`;
